// Refined, corporate product mockups
import React from 'react';
import { CalendarDays, Users, BarChart3, MapPin, Video, TrendingUp } from 'lucide-react';

const WindowChrome = ({ title = 'briefingiq.app' }) => (
  <div className="window-chrome">
    <span className="window-dot bg-[#ff5f57]" />
    <span className="window-dot bg-[#febc2e]" />
    <span className="window-dot bg-[#28c840]" />
    <div className="ml-4 flex-1 flex justify-center">
      <div className="text-[11px] text-ink-3 bg-white/70 px-3 py-0.5 rounded border border-[var(--line-2)]">
        {title}
      </div>
    </div>
  </div>
);

export const BriefingIQMockup = () => (
  <div className="w-full rounded-2xl bg-white border border-[var(--line-2)] overflow-hidden shadow-sm">
    <WindowChrome title="briefingiq.app/dashboard" />
    <div className="grid grid-cols-12 min-h-[400px]">
      {/* Sidebar */}
      <div className="col-span-3 border-r border-[var(--line-2)] p-4 bg-[var(--bg-alt)]">
        <div className="text-[10px] uppercase tracking-widest text-ink-3 mb-3">Workspace</div>
        <div className="space-y-1.5">
          {['Dashboard', 'Briefings', 'Accounts', 'Locations', 'Analytics'].map((l, i) => (
            <div
              key={l}
              className={`text-[12px] px-3 py-1.5 rounded-lg ${
                i === 0 ? 'bg-white text-ink font-medium shadow-sm' : 'text-ink-2'
              }`}
            >
              {l}
            </div>
          ))}
        </div>
        <div className="mt-6 text-[10px] uppercase tracking-widest text-ink-3 mb-3">Team</div>
        <div className="flex -space-x-1.5">
          {['SY', 'JM', 'AK', 'RB'].map((n, i) => (
            <div
              key={i}
              className="w-6 h-6 rounded-full border-2 border-[var(--bg-alt)] text-[9px] flex items-center justify-center text-white font-medium"
              style={{ background: ['#4F46E5', '#6366F1', '#818CF8', '#C7D2FE'][i] }}
            >
              {n}
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="col-span-9 p-5 space-y-4">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-3">Overview · Q1 2026</div>
            <div className="text-lg font-semibold text-ink mt-0.5">Executive Briefings</div>
          </div>
          <div className="text-[11px] text-ink-3">Updated 2 min ago</div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Scheduled', value: '24', trend: '+12%' },
            { label: 'Completed', value: '18', trend: '+8%' },
            { label: 'NPS', value: '72', trend: '+4' },
            { label: 'Pipeline', value: '$4.2M', trend: '+22%' },
          ].map((m) => (
            <div key={m.label} className="rounded-xl border border-[var(--line-2)] p-3">
              <div className="text-lg font-semibold text-ink">{m.value}</div>
              <div className="text-[10px] text-ink-3 mt-0.5">{m.label}</div>
              <div className="text-[10px] text-ink-2 mt-1 tabular-nums">{m.trend}</div>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <div className="rounded-xl border border-[var(--line-2)] p-4">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[11px] font-medium text-ink">Briefings by week</div>
            <div className="text-[10px] text-ink-3">Last 8 weeks</div>
          </div>
          <div className="flex items-end gap-2 h-20">
            {[40, 65, 50, 78, 62, 88, 72, 95].map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-t bg-[#4F46E5]"
                  style={{ height: `${h}%`, opacity: 0.15 + (i * 0.11) }}
                />
                <div className="text-[9px] text-ink-3">W{i + 1}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent list */}
        <div className="rounded-xl border border-[var(--line-2)] divide-y divide-[var(--line-2)]">
          {[
            { name: 'Acme Corp — Product Roadmap Briefing', city: 'New York', time: 'Mon 10:00' },
            { name: 'Globex Inc — Executive Discovery', city: 'San Francisco', time: 'Wed 14:00' },
            { name: 'Initech — Strategy Workshop', city: 'London', time: 'Fri 16:30' },
          ].map((b, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded bg-[var(--bg-alt)] flex items-center justify-center">
                  <MapPin className="w-3 h-3 text-ink-2" />
                </div>
                <div>
                  <div className="text-[12px] text-ink font-medium">{b.name}</div>
                  <div className="text-[10px] text-ink-3">{b.city}</div>
                </div>
              </div>
              <div className="text-[11px] text-ink-2 tabular-nums">{b.time}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const EventIQMockup = () => (
  <div className="w-full rounded-2xl bg-white border border-[var(--line-2)] overflow-hidden shadow-sm">
    <WindowChrome title="eventiq.app/events/annual-summit" />
    <div className="p-6 space-y-5 min-h-[400px]">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-3">Event overview</div>
        <div className="flex items-baseline justify-between mt-1">
          <div className="text-lg font-semibold text-ink">Annual Summit 2026</div>
          <div className="text-[11px] px-2 py-0.5 rounded-full bg-[#4F46E5] text-white font-medium">Live</div>
        </div>
      </div>

      {/* Hero metrics */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Users, label: 'Attendees', value: '1,240', sub: '+186 this week' },
          { icon: CalendarDays, label: 'Sessions', value: '48', sub: '32 speakers' },
          { icon: Video, label: 'Hybrid', value: '68%', sub: 'in-person' },
        ].map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.label} className="rounded-xl bg-[var(--bg-alt)] p-4">
              <Icon className="w-4 h-4 text-ink mb-3" strokeWidth={1.5} />
              <div className="text-2xl font-semibold text-ink">{m.value}</div>
              <div className="text-[10px] text-ink-3 mt-1">{m.label}</div>
              <div className="text-[10px] text-ink-2 mt-2">{m.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Line chart */}
      <div className="rounded-xl border border-[var(--line-2)] p-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] font-medium text-ink flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Registration trend
          </div>
          <div className="text-[10px] text-ink-3">Last 30 days</div>
        </div>
        <svg viewBox="0 0 300 80" className="w-full h-16">
          <defs>
            <linearGradient id="lineFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#4F46E5" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,70 L30,60 L60,55 L90,50 L120,42 L150,38 L180,30 L210,28 L240,20 L270,15 L300,10 L300,80 L0,80 Z"
            fill="url(#lineFill)"
          />
          <path
            d="M0,70 L30,60 L60,55 L90,50 L120,42 L150,38 L180,30 L210,28 L240,20 L270,15 L300,10"
            fill="none"
            stroke="#4F46E5"
            strokeWidth="1.5"
          />
        </svg>
      </div>

      {/* Sessions list */}
      <div className="rounded-xl border border-[var(--line-2)] divide-y divide-[var(--line-2)]">
        {[
          { title: 'Opening Keynote', speaker: 'S. Yadavalli', time: '09:00', room: 'Main Hall' },
          { title: 'AI in Enterprise Workflows', speaker: 'Panel of 4', time: '10:30', room: 'Studio A' },
          { title: 'Customer Success Deep-Dive', speaker: 'J. Martinez', time: '13:00', room: 'Studio B' },
        ].map((s, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="text-[11px] font-mono text-ink-3 tabular-nums w-10">{s.time}</div>
              <div>
                <div className="text-[12px] text-ink font-medium">{s.title}</div>
                <div className="text-[10px] text-ink-3">{s.speaker} · {s.room}</div>
              </div>
            </div>
            <BarChart3 className="w-3.5 h-3.5 text-ink-3" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const PlatformMockup = () => (
  <div className="w-full rounded-2xl bg-white border border-[var(--line-2)] overflow-hidden shadow-sm">
    <WindowChrome title="platform.briefingiq.app" />
    <div className="p-6 grid grid-cols-2 gap-4">
      {[
        { name: 'Salesforce', status: 'Connected', c: 'S' },
        { name: 'Microsoft Outlook', status: 'Connected', c: 'O' },
        { name: 'Zoom', status: 'Connected', c: 'Z' },
        { name: 'Microsoft Teams', status: 'Connected', c: 'T' },
      ].map((s) => (
        <div key={s.name} className="flex items-center justify-between p-4 rounded-xl border border-[var(--line-2)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--bg-alt)] flex items-center justify-center font-semibold text-ink text-sm">
              {s.c}
            </div>
            <div>
              <div className="text-[13px] font-medium text-ink">{s.name}</div>
              <div className="text-[11px] text-ink-3 flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 soft-pulse" />
                {s.status}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);
